import React, { useEffect, useState } from 'react';
import { Save, Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import adminApi from '../api/adminApi';
import BilingualField from '../components/BilingualField';
import ImagePicker from '../components/ImagePicker';

const inputClass = "w-full bg-[#1A1A1A] border border-[rgba(212,175,55,0.2)] rounded-sm px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#D4AF37] transition-colors";
const labelClass = "block text-xs uppercase tracking-wider text-[#D4AF37] font-semibold mb-2";
const Card = ({ title, children }) => (
  <div className="bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm p-5 space-y-4">
    <h2 className="text-sm font-semibold text-white border-b border-[rgba(212,175,55,0.1)] pb-3">{title}</h2>
    {children}
  </div>
);

const emptyValue = () => ({
  id: `val-${Date.now()}`,
  icon: 'target',
  title_en: '', title_ar: '',
  description_en: '', description_ar: '',
  badge_en: '', badge_ar: '',
  tagline_en: '', tagline_ar: '',
  timeline: [],
  highlights_en: [], highlights_ar: [],
  enabled: true,
  order: 99,
});

const AboutEditorPage = () => {
  const [about, setAbout] = useState({});
  const [translations, setTranslations] = useState({ en: {}, ar: {} });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get('/admin/content').then(r => {
      setAbout(r.data?.about || {});
      setTranslations(r.data?.translations || { en: {}, ar: {} });
    }).catch(() => toast.error('Failed to load content'));
  }, []);

  const setTrans = (lang, section, key, value) => {
    setTranslations(prev => ({
      ...prev,
      [lang]: { ...prev[lang], [section]: { ...(prev[lang]?.[section] || {}), [key]: value } }
    }));
  };

  const setStatField = (lang, key, value) => setTrans(lang, 'about', key, value);

  const moveValue = (index, dir) => {
    const vals = [...(about.values || [])];
    const swap = index + dir;
    if (swap < 0 || swap >= vals.length) return;
    [vals[index], vals[swap]] = [vals[swap], vals[index]];
    setAbout(a => ({ ...a, values: vals.map((v, i) => ({ ...v, order: i + 1 })) }));
  };

  const updateValue = (index, updated) => {
    setAbout(a => ({ ...a, values: (a.values || []).map((v, i) => i === index ? updated : v) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        adminApi.patch('/admin/content/about', about),
        adminApi.patch('/admin/content/translations', translations),
      ]);
      toast.success('About section saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const en = translations.en || {};
  const ar = translations.ar || {};

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">About Section</h1>
          <p className="text-white/40 text-sm mt-0.5">Company story, stats, values, and highlights</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-black font-semibold text-sm rounded-sm hover:bg-[#C5A028] transition-colors disabled:opacity-50">
          <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <Card title="Section Header">
        <BilingualField label="Overline" valueEn={en.about?.overline || ''} valueAr={ar.about?.overline || ''} onChangeEn={v => setTrans('en', 'about', 'overline', v)} onChangeAr={v => setTrans('ar', 'about', 'overline', v)} />
        <BilingualField label="Title" valueEn={en.about?.title || ''} valueAr={ar.about?.title || ''} onChangeEn={v => setTrans('en', 'about', 'title', v)} onChangeAr={v => setTrans('ar', 'about', 'title', v)} />
        <BilingualField label="Description 1" type="textarea" valueEn={en.about?.description1 || ''} valueAr={ar.about?.description1 || ''} onChangeEn={v => setTrans('en', 'about', 'description1', v)} onChangeAr={v => setTrans('ar', 'about', 'description1', v)} />
        <BilingualField label="Description 2" type="textarea" valueEn={en.about?.description2 || ''} valueAr={ar.about?.description2 || ''} onChangeEn={v => setTrans('en', 'about', 'description2', v)} onChangeAr={v => setTrans('ar', 'about', 'description2', v)} />
      </Card>

      <Card title="Statistics">
        {[
          { valueKey: 'employees', labelKey: 'employeesLabel', label: 'Employees Stat' },
          { valueKey: 'experience', labelKey: 'experienceLabel', label: 'Experience Stat' },
          { valueKey: 'markets', labelKey: 'marketsLabel', label: 'Markets Stat' },
        ].map(stat => (
          <div key={stat.valueKey} className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4 border-b border-[rgba(212,175,55,0.08)] last:border-0 last:pb-0">
            <div>
              <label className={labelClass}>{stat.label} — Value</label>
              <input value={en.about?.stats?.[stat.valueKey] || ''} onChange={e => { const s = { ...(en.about?.stats || {}), [stat.valueKey]: e.target.value }; setTrans('en', 'about', 'stats', s); setTrans('ar', 'about', 'stats', { ...(ar.about?.stats || {}), [stat.valueKey]: e.target.value }); }} className={inputClass} placeholder="e.g. 1,200+" />
            </div>
            <BilingualField label={`${stat.label} — Label`} valueEn={en.about?.stats?.[stat.labelKey] || ''} valueAr={ar.about?.stats?.[stat.labelKey] || ''} onChangeEn={v => setTrans('en', 'about', 'stats', { ...(en.about?.stats || {}), [stat.labelKey]: v })} onChangeAr={v => setTrans('ar', 'about', 'stats', { ...(ar.about?.stats || {}), [stat.labelKey]: v })} />
          </div>
        ))}
      </Card>

      <Card title="About Image">
        <ImagePicker label="Company / Headquarters Image" value={about.image_url || ''} onChange={v => setAbout(a => ({ ...a, image_url: v }))} />
      </Card>

      <Card title="Highlights List">
        <div className="space-y-2">
          {(about.highlights_en || []).map((item, i) => (
            <div key={i} className="grid grid-cols-2 gap-2 items-center">
              <input value={item} onChange={e => { const arr = [...(about.highlights_en || [])]; arr[i] = e.target.value; setAbout(a => ({ ...a, highlights_en: arr })); }} className={inputClass} placeholder="English highlight" />
              <input value={(about.highlights_ar || [])[i] || ''} onChange={e => { const arr = [...(about.highlights_ar || [])]; arr[i] = e.target.value; setAbout(a => ({ ...a, highlights_ar: arr })); }} dir="rtl" className={`${inputClass} text-right`} placeholder="Arabic highlight" />
            </div>
          ))}
          <button onClick={() => setAbout(a => ({ ...a, highlights_en: [...(a.highlights_en || []), ''], highlights_ar: [...(a.highlights_ar || []), ''] }))} className="flex items-center gap-2 text-[#D4AF37] text-xs hover:text-[#C5A028] transition-colors mt-2">
            <Plus className="w-3.5 h-3.5" />Add Highlight
          </button>
        </div>
      </Card>

      <Card title="Values Cards">
        <BilingualField label="Section Heading" valueEn={en.about?.valuesHeading || ''} valueAr={ar.about?.valuesHeading || ''} onChangeEn={v => setTrans('en', 'about', 'valuesHeading', v)} onChangeAr={v => setTrans('ar', 'about', 'valuesHeading', v)} />
        <div className="space-y-3">
          {(about.values || []).map((value, index) => {
            const enabled = value.enabled !== false;
            const timeline = value.timeline || [];
            const highlightsEn = value.highlights_en || [];
            const highlightsAr = value.highlights_ar || [];
            return (
            <div key={value.id} className="bg-[#1A1A1A] border border-[rgba(212,175,55,0.15)] rounded-sm p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-white/50 text-xs">Value {index + 1}</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => updateValue(index, { ...value, enabled: !enabled })}
                    className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${enabled ? 'bg-[#D4AF37]' : 'bg-white/10'}`}
                    aria-pressed={enabled}
                    title={enabled ? 'Visible — click to hide' : 'Hidden — click to show'}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-7' : 'translate-x-1'}`} />
                  </button>
                  <div className="flex gap-1">
                    <button onClick={() => moveValue(index, -1)} disabled={index === 0} className="w-6 h-6 flex items-center justify-center text-white/30 hover:text-white disabled:opacity-20 transition-colors"><ChevronUp className="w-3.5 h-3.5" /></button>
                    <button onClick={() => moveValue(index, 1)} disabled={index === (about.values?.length || 0) - 1} className="w-6 h-6 flex items-center justify-center text-white/30 hover:text-white disabled:opacity-20 transition-colors"><ChevronDown className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setAbout(a => ({ ...a, values: (a.values || []).filter((_, i) => i !== index) }))} className="w-6 h-6 flex items-center justify-center text-white/20 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
              <div>
                <label className={labelClass}>Icon</label>
                <select value={value.icon} onChange={e => updateValue(index, { ...value, icon: e.target.value })} className={inputClass}>
                  <option value="target">Target</option>
                  <option value="lightbulb">Lightbulb</option>
                  <option value="users">Users</option>
                  <option value="award">Award</option>
                  <option value="star">Star</option>
                </select>
              </div>
              <BilingualField label="Title" valueEn={value.title_en} valueAr={value.title_ar} onChangeEn={v => updateValue(index, { ...value, title_en: v })} onChangeAr={v => updateValue(index, { ...value, title_ar: v })} />
              <BilingualField label="Description" type="textarea" valueEn={value.description_en} valueAr={value.description_ar} onChangeEn={v => updateValue(index, { ...value, description_en: v })} onChangeAr={v => updateValue(index, { ...value, description_ar: v })} />

              <div className="border-t border-[rgba(212,175,55,0.1)] pt-3 space-y-3">
                <p className="text-[10px] uppercase tracking-wider text-white/40">Detail Modal (shown when card is clicked)</p>
                <BilingualField label="Badge (small label above title)" valueEn={value.badge_en || ''} valueAr={value.badge_ar || ''} onChangeEn={v => updateValue(index, { ...value, badge_en: v })} onChangeAr={v => updateValue(index, { ...value, badge_ar: v })} />
                <BilingualField label="Tagline (italic line under title)" valueEn={value.tagline_en || ''} valueAr={value.tagline_ar || ''} onChangeEn={v => updateValue(index, { ...value, tagline_en: v })} onChangeAr={v => updateValue(index, { ...value, tagline_ar: v })} />

                <div>
                  <label className={labelClass}>Key Milestones (Timeline)</label>
                  <p className="text-[10px] text-white/40 mb-2">Year + event description. Leave empty to hide this section in the modal.</p>
                  <div className="space-y-2">
                    {timeline.map((item, ti) => (
                      <div key={ti} className="grid grid-cols-12 gap-2 items-start">
                        <input
                          value={item.year || ''}
                          onChange={e => {
                            const arr = [...timeline];
                            arr[ti] = { ...arr[ti], year: e.target.value };
                            updateValue(index, { ...value, timeline: arr });
                          }}
                          className={`${inputClass} col-span-2`}
                          placeholder="Year"
                        />
                        <input
                          value={item.event_en || ''}
                          onChange={e => {
                            const arr = [...timeline];
                            arr[ti] = { ...arr[ti], event_en: e.target.value };
                            updateValue(index, { ...value, timeline: arr });
                          }}
                          className={`${inputClass} col-span-5`}
                          placeholder="Event (English)"
                        />
                        <input
                          value={item.event_ar || ''}
                          onChange={e => {
                            const arr = [...timeline];
                            arr[ti] = { ...arr[ti], event_ar: e.target.value };
                            updateValue(index, { ...value, timeline: arr });
                          }}
                          dir="rtl"
                          className={`${inputClass} col-span-4 text-right`}
                          placeholder="الحدث (عربي)"
                        />
                        <button
                          onClick={() => {
                            const arr = timeline.filter((_, i) => i !== ti);
                            updateValue(index, { ...value, timeline: arr });
                          }}
                          className="col-span-1 w-7 h-7 flex items-center justify-center text-white/20 hover:text-red-400 transition-colors mt-1"
                          title="Remove milestone"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => updateValue(index, { ...value, timeline: [...timeline, { year: '', event_en: '', event_ar: '' }] })}
                      className="flex items-center gap-2 text-[#D4AF37] text-xs hover:text-[#C5A028] transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />Add Milestone
                    </button>
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Highlights</label>
                  <p className="text-[10px] text-white/40 mb-2">Bullet points. Leave empty to hide this section in the modal.</p>
                  <div className="space-y-2">
                    {Math.max(highlightsEn.length, highlightsAr.length) === 0 && (
                      <p className="text-white/30 text-xs italic">No highlights yet.</p>
                    )}
                    {Array.from({ length: Math.max(highlightsEn.length, highlightsAr.length) }).map((_, hi) => (
                      <div key={hi} className="grid grid-cols-12 gap-2 items-start">
                        <input
                          value={highlightsEn[hi] || ''}
                          onChange={e => {
                            const arr = [...highlightsEn];
                            arr[hi] = e.target.value;
                            updateValue(index, { ...value, highlights_en: arr });
                          }}
                          className={`${inputClass} col-span-6`}
                          placeholder="Highlight (English)"
                        />
                        <input
                          value={highlightsAr[hi] || ''}
                          onChange={e => {
                            const arr = [...highlightsAr];
                            arr[hi] = e.target.value;
                            updateValue(index, { ...value, highlights_ar: arr });
                          }}
                          dir="rtl"
                          className={`${inputClass} col-span-5 text-right`}
                          placeholder="نقطة (عربي)"
                        />
                        <button
                          onClick={() => {
                            const arrEn = highlightsEn.filter((_, i) => i !== hi);
                            const arrAr = highlightsAr.filter((_, i) => i !== hi);
                            updateValue(index, { ...value, highlights_en: arrEn, highlights_ar: arrAr });
                          }}
                          className="col-span-1 w-7 h-7 flex items-center justify-center text-white/20 hover:text-red-400 transition-colors mt-1"
                          title="Remove highlight"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => updateValue(index, { ...value, highlights_en: [...highlightsEn, ''], highlights_ar: [...highlightsAr, ''] })}
                      className="flex items-center gap-2 text-[#D4AF37] text-xs hover:text-[#C5A028] transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />Add Highlight
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
          })}
          <button onClick={() => setAbout(a => ({ ...a, values: [...(a.values || []), emptyValue()] }))} className="flex items-center gap-2 text-[#D4AF37] text-xs hover:text-[#C5A028] transition-colors">
            <Plus className="w-3.5 h-3.5" />Add Value Card
          </button>
        </div>
      </Card>
    </div>
  );
};

export default AboutEditorPage;
